import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  AlertCircle, Banknote, BarChart3, CheckCircle2, Clock3, Euro,
  ReceiptText, WalletCards, Wrench,
} from "lucide-react";
import { fmtEurCompact as fmtEur } from "@/lib/formatMoney";

const fmtHours = (seconds) => {
  const minutes = Math.round((seconds || 0) / 60);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours ? `${hours} Std. ${rest ? `${rest} Min.` : ""}` : `${rest} Min.`;
};
const todayIso = () => new Date().toISOString().slice(0, 10);
const monthStartIso = () => `${todayIso().slice(0, 7)}-01`;
const PERIODS = [
  { key: "this_month", label: "Dieser Monat" },
  { key: "last_month", label: "Letzter Monat" },
  { key: "this_year", label: "Dieses Jahr" },
  { key: "custom", label: "Zeitraum" },
];

function Stat({ icon: Icon, label, value, help, meta, tone = "blue", testid }) {
  const tones = {
    green: "bg-emerald-50 text-emerald-700",
    blue: "bg-blue-50 text-blue-700",
    amber: "bg-amber-50 text-amber-700",
    violet: "bg-violet-50 text-violet-700",
  };
  return (
    <div data-testid={testid} className="premium-card stellar-stat-card p-5" title={help}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
        <span className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${tones[tone]}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{value}</div>
      {meta && <div className="mt-1 text-xs text-muted-foreground">{meta}</div>}
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border bg-white/95 p-3 shadow-lg text-xs">
      <div className="font-semibold mb-2">{label}</div>
      {payload.map((item) => (
        <div key={item.dataKey} className="flex justify-between gap-6" style={{ color: item.color }}>
          <span>{item.name}</span><span className="font-semibold">{fmtEur(item.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const [period, setPeriod] = useState("this_month");
  const [start, setStart] = useState(monthStartIso());
  const [end, setEnd] = useState(todayIso());

  const params = useMemo(() => ({
    period,
    start: period === "custom" ? start : undefined,
    end: period === "custom" ? end : undefined,
  }), [period, start, end]);

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", params],
    queryFn: async () => (await api.get("/dashboard", { params })).data,
  });

  const summary = data?.summary || {};
  const maxService = Math.max(1, ...(data?.service_stats || []).map((row) => row.value || row.count || 0));
  const maxMethod = Math.max(1, ...(data?.payment_methods || []).map((row) => row.value || 0));

  return (
    <div className="stellar-page max-w-7xl">
      <div className="stellar-page-heading stellar-heading-blue mb-6 flex-wrap gap-4">
        <span className="stellar-module-icon stellar-module-blue"><BarChart3 className="h-5 w-5" /></span>
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight" title="Aufträge, Zahlungen und Werkstattleistung auf einen Blick.">Statistiken</h1>
        </div>
        <div className="w-full lg:w-auto lg:ml-auto">
          <div className="stellar-segmented overflow-x-auto">
            {PERIODS.map((item) => (
              <button
                key={item.key}
                onClick={() => setPeriod(item.key)}
                className={`px-3.5 py-1.5 text-sm rounded-lg whitespace-nowrap ${period === item.key ? "bg-white text-blue-700 shadow-sm font-semibold" : "text-muted-foreground"}`}
                data-testid={`dashboard-period-${item.key}`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {period === "custom" && (
        <div className="mb-5 flex flex-wrap items-end gap-3 premium-card p-4">
          <label className="text-xs text-muted-foreground">Von<Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 w-44" /></label>
          <label className="text-xs text-muted-foreground">Bis<Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 w-44" /></label>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat testid="stat-revenue" icon={Banknote} label="Umsatz" value={isLoading ? "…" : fmtEur(summary.revenue)} help="Tatsächlich erhaltene Zahlungen im gewählten Zeitraum." tone="green" />
        <Stat testid="stat-order-value" icon={Wrench} label="Auftragswert" value={isLoading ? "…" : fmtEur(summary.order_value)} help="Gesamtwert der abgeschlossenen Arbeiten im gewählten Zeitraum." tone="blue" />
        <Stat testid="stat-outstanding" icon={AlertCircle} label="Offene Beträge" value={isLoading ? "…" : fmtEur(summary.outstanding)} help="Noch nicht bezahlte Beträge aus Rechnungen und abgeschlossenen Aufträgen." meta={`${data?.open_invoices_count || 0} offene Rechnungen · ${data?.unpaid_jobs_count || 0} unbezahlte Aufträge`} tone="amber" />
        <Stat testid="stat-completed" icon={CheckCircle2} label="Abgeschlossen" value={isLoading ? "…" : (summary.completed_jobs || 0)} help="Anzahl der abgeschlossenen Aufträge im gewählten Zeitraum." tone="violet" />
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div className="premium-card p-4 flex items-center gap-4" data-testid="stat-invoice-revenue" title="Erhaltene Zahlungen, die einer Rechnung zugeordnet sind.">
          <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-700"><ReceiptText className="h-5 w-5" /></span>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Rechnungszahlungen</div>
            <div className="font-display text-2xl font-bold mt-1">{isLoading ? "…" : fmtEur(summary.invoice_revenue)}</div>
          </div>
        </div>
        <div className="premium-card p-4 flex items-center gap-4" data-testid="stat-direct-revenue" title="Erhaltene Direktzahlungen ohne Rechnung.">
          <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-700"><Euro className="h-5 w-5" /></span>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Direktzahlungen</div>
            <div className="font-display text-2xl font-bold mt-1">{isLoading ? "…" : fmtEur(summary.direct_revenue)}</div>
          </div>
        </div>
      </div>

      <div className="premium-card p-5 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
          <div>
            <h2 className="font-display text-lg font-semibold" title="Zahlungseingang im Vergleich zum Wert abgeschlossener Aufträge.">Entwicklung</h2>
          </div>
          <div className="flex gap-4 text-xs">
            <span className="inline-flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-full bg-emerald-500" />Umsatz</span>
            <span className="inline-flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-full bg-blue-500" />Auftragswert</span>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data?.revenue_series || []} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10B981" stopOpacity={0.28} /><stop offset="95%" stopColor="#10B981" stopOpacity={0} /></linearGradient>
                <linearGradient id="ordersFill" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3B82F6" stopOpacity={0.18} /><stop offset="95%" stopColor="#3B82F6" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} minTickGap={24} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v} €`} tickLine={false} axisLine={false} width={58} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="order_value" name="Auftragswert" stroke="#3B82F6" fill="url(#ordersFill)" strokeWidth={2} />
              <Area type="monotone" dataKey="revenue" name="Umsatz" stroke="#10B981" fill="url(#revenueFill)" strokeWidth={2.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6 mb-6">
        <div className="lg:col-span-3 premium-card overflow-hidden">
          <div className="p-5 border-b border-border/60">
            <h2 className="font-display text-lg font-semibold" title="Auswertung der Leistungen aus abgeschlossenen Aufträgen.">Leistungen</h2>
          </div>
          <div className="p-5 space-y-4">
            {(data?.service_stats || []).length === 0 && <div className="py-8 text-center text-sm text-muted-foreground">Keine abgeschlossenen Leistungen im Zeitraum.</div>}
            {(data?.service_stats || []).slice(0, 8).map((row) => (
              <div key={row.name}>
                <div className="flex items-center justify-between gap-3 text-sm mb-1.5">
                  <div className="min-w-0 flex items-center gap-2"><i className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: row.color || "#64748B" }} /><span className="truncate font-medium">{row.name}</span><span className="text-xs text-muted-foreground">× {row.count}</span></div>
                  <span className="font-semibold">{fmtEur(row.value)}</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.max(4, (row.value / maxService) * 100)}%`, background: row.color || "#64748B" }} /></div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 premium-card overflow-hidden">
          <div className="p-5 border-b border-border/60">
            <h2 className="font-display text-lg font-semibold" title="Verteilung der tatsächlichen Einnahmen nach Zahlungsart.">Zahlungsarten</h2>
          </div>
          <div className="p-5 space-y-4">
            {(data?.payment_methods || []).length === 0 && <div className="py-8 text-center text-sm text-muted-foreground">Keine Zahlungen im Zeitraum.</div>}
            {(data?.payment_methods || []).map((row) => (
              <div key={row.key}>
                <div className="flex justify-between text-sm mb-1.5"><span>{row.name}</span><span className="font-semibold">{fmtEur(row.value)}</span></div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-teal-500" style={{ width: `${Math.max(4, (row.value / maxMethod) * 100)}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="premium-card p-5">
          <div className="flex items-center gap-2 mb-5"><Clock3 className="h-5 w-5 text-orange-500" /><h2 className="font-display font-semibold">Arbeitsstatistik</h2></div>
          <div className="space-y-4 text-sm">
            <div className="flex justify-between border-b border-border/60 pb-3"><span className="text-muted-foreground">Gesamte Arbeitszeit</span><strong>{fmtHours(summary.total_work_seconds)}</strong></div>
            <div className="flex justify-between border-b border-border/60 pb-3"><span className="text-muted-foreground">Ø Zeit pro Auftrag</span><strong>{fmtHours(summary.average_work_seconds)}</strong></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Ø Auftragswert</span><strong>{fmtEur(summary.average_order_value)}</strong></div>
          </div>
        </div>

        <div className="lg:col-span-2 premium-card overflow-hidden">
          <div className="p-5 border-b border-border/60 flex items-center gap-2">
            <WalletCards className="h-5 w-5 text-amber-600" />
            <h2 className="font-display font-semibold" title="Offene Rechnungen und aktive Aufträge.">Aufmerksamkeit erforderlich</h2>
          </div>
          <div className="divide-y divide-border/60">
            {(data?.attention || []).length === 0 && <div className="p-8 text-center text-sm text-emerald-700"><CheckCircle2 className="h-8 w-8 mx-auto mb-2 opacity-60" />Alles erledigt.</div>}
            {(data?.attention || []).map((item) => (
              <Link key={`${item.kind}-${item.id}`} to={item.href} className="flex items-center gap-3 p-4 hover:bg-muted/40">
                <span className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${item.kind === "invoice" ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-blue-700"}`}>
                  {item.kind === "invoice" ? <ReceiptText className="h-4 w-4" /> : <Wrench className="h-4 w-4" />}
                </span>
                <div className="flex-1 min-w-0"><div className="font-medium truncate">{item.title}</div><div className="text-xs text-muted-foreground truncate">{item.subtitle}</div></div>
                <div className="text-right"><div className="font-semibold text-sm">{fmtEur(item.amount)}</div><div className="text-xs text-primary">Öffnen →</div></div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
